import React, { Component, ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error | null;
  componentStack?: string | null;
}

export class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, componentStack: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Keep a console warning for diagnostics, but do not persist error details to localStorage.
    console.error('AuthErrorBoundary caught an error:', error, errorInfo);
    // capture component stack for display in dev and store to window for easy retrieval
    const componentStack = errorInfo?.componentStack ?? null;
    this.setState({ error, componentStack });
    try {
      (window as any).__lastAuthError = { message: error?.message, stack: (error as any)?.stack ?? null, componentStack, time: new Date().toISOString() };
    } catch (e) {
      // ignore
    }
  }

  handleReload = () => {
    try { window.location.reload(); } catch (_) { /* ignore */ }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isDev = process.env.NODE_ENV !== 'production';

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Alert className="max-w-2xl">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Authentication Error</AlertTitle>
            <AlertDescription>
              There was an issue with authentication. Please refresh the page or try again later.
              {isDev && this.state.error && (
                <div className="mt-3 text-sm text-muted-foreground">
                  <strong>Error:</strong> {this.state.error.message}
                  {this.state.componentStack && (
                    <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-auto">{this.state.componentStack}</pre>
                  )}
                </div>
              )}
            </AlertDescription>
            <div className="mt-4 flex justify-end">
              <button onClick={this.handleReload} className="btn btn-sm">Reload</button>
            </div>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}